import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  Button,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { toast } from "react-toastify";
import Api from "../../api/Api";
import { Urls } from "../../api/Urls";
import { useAuthContext } from "../../hooks/useAuthContext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import IconButton from "@mui/material/IconButton";

function NotificationCampaignDetail() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const access_token = user?.access_token;
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resendCampaignDialogOpen, setResendCampaignDialogOpen] = useState(false);
  const [resendRecipientDialogOpen, setResendRecipientDialogOpen] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);

  useEffect(() => {
    fetchCampaign();
  }, [campaignId]);

  const fetchCampaign = () => {
    setLoading(true);
    Api.get(Urls.GET_NOTIFICATION_CAMPAIGN + campaignId, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
      params: {
        include_recipients: true,
      },
    })
      .then((res) => {
        setCampaign(res?.data?.data);
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Error loading campaign");
        setLoading(false);
      });
  };

  const handleResendCampaignClick = () => {
    setResendCampaignDialogOpen(true);
  };

  const handleResendCampaignConfirm = async () => {
    setResendCampaignDialogOpen(false);
    try {
      await Api.post(Urls.RESEND_NOTIFICATION_CAMPAIGN + campaignId + "/resend", {}, {
        headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
      });
      toast.success("Campaign resent successfully");
      fetchCampaign();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error resending campaign");
    }
  };

  const handleResendToRecipientClick = (recipient) => {
    setSelectedRecipient(recipient);
    setResendRecipientDialogOpen(true);
  };

  const handleResendToRecipientConfirm = async () => {
    if (!selectedRecipient) return;
    
    setResendRecipientDialogOpen(false);
    try {
      await Api.post(
        Urls.RESEND_NOTIFICATION_RECIPIENT + campaignId + "/resend-recipient",
        { user_id: selectedRecipient.user_id },
        {
          headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
        }
      );
      toast.success("Campaign resent to recipient successfully");
      setSelectedRecipient(null);
      fetchCampaign();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error resending to recipient");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "dispatched":
        return "success";
      case "scheduled":
        return "info";
      case "draft":
        return "default";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "dispatched":
        return "Sent";
      case "scheduled":
        return "Scheduled";
      case "draft":
        return "Draft";
      case "failed":
        return "Failed";
      default:
        return status;
    }
  };

  const getTargetLabel = (targetType) => {
    switch (targetType) {
      case "all_clients":
        return "All Clients";
      case "all_coaches":
        return "All Coaches";
      case "all_admins":
        return "All Admins";
      case "program_duration":
        return "Specific Program";
      case "court_weekly_schedule":
        return "Specific Weekly Schedule";
      default:
        return targetType;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box mt={4}>
        <Typography>Campaign not found</Typography>
      </Box>
    );
  }

  const readRate = campaign.recipients_count > 0
    ? ((campaign.read_count / campaign.recipients_count) * 100).toFixed(1)
    : 0;

  return (
    <Box mt={4}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/admin/notifications/campaigns")}
          >
            Back
          </Button>
          <Typography fontSize="18px" fontWeight="500" color="#4B465C">
            Campaign Details
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={handleResendCampaignClick}
        >
          Resend Campaign
        </Button>
      </Box>
      <Divider />

      <Grid container spacing={3} mt={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" mb={2}>
              {campaign.name}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Title
              </Typography>
              <Typography>{campaign.title}</Typography>
            </Box>

            {campaign.body && (
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Message
                </Typography>
                <Typography>{campaign.body}</Typography>
              </Box>
            )}

            {campaign.payload?.cta_link && (
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  CTA Link
                </Typography>
                <Typography>{campaign.payload.cta_link}</Typography>
              </Box>
            )}

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                Delivery Channels
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {campaign.channels?.map((channel) => (
                  <Chip
                    key={channel}
                    label={
                      channel === "in_app"
                        ? "In-app"
                        : channel === "push"
                        ? "Push"
                        : channel === "email"
                        ? "Email"
                        : channel
                    }
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" mb={2}>
              Statistics
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={getStatusLabel(campaign.status)}
                color={getStatusColor(campaign.status)}
                sx={{ mt: 1 }}
              />
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Target Type
              </Typography>
              <Typography>{getTargetLabel(campaign.target_type)}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Recipients
              </Typography>
              <Typography variant="h6">{campaign.recipients_count || 0}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Read
              </Typography>
              <Typography variant="h6">{campaign.read_count || 0}</Typography>
              <Typography variant="caption" color="text.secondary">
                ({readRate}% read rate)
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Created At
              </Typography>
              <Typography>
                {new Date(campaign.created_at).toLocaleString("en-US")}
              </Typography>
            </Box>

            {campaign.dispatched_at && (
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Sent At
                </Typography>
                <Typography>
                  {new Date(campaign.dispatched_at).toLocaleString("en-US")}
                </Typography>
              </Box>
            )}

            {campaign.sent_by && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Sent By
                </Typography>
                <Typography>{campaign.sent_by.name}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {campaign?.recipients && campaign.recipients.length > 0 && (
        <Box mt={3}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" mb={2}>
              Recipients
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Read At</TableCell>
                    <TableCell>Delivered At</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaign.recipients.map((recipient) => (
                    <TableRow key={recipient.user_id}>
                      <TableCell>{recipient.user_name || "N/A"}</TableCell>
                      <TableCell>{recipient.user_email || "N/A"}</TableCell>
                      <TableCell>
                        <Chip
                          label={recipient.delivery_status || "pending"}
                          size="small"
                          color={
                            recipient.delivery_status === "sent"
                              ? "success"
                              : recipient.delivery_status === "failed"
                              ? "error"
                              : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {recipient.read_at
                          ? new Date(recipient.read_at).toLocaleString("en-US")
                          : "Not read"}
                      </TableCell>
                      <TableCell>
                        {recipient.delivered_at
                          ? new Date(recipient.delivered_at).toLocaleString("en-US")
                          : "Not delivered"}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleResendToRecipientClick(recipient)}
                          title="Resend to this recipient"
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* Dialog for resending entire campaign */}
      <Dialog
        open={resendCampaignDialogOpen}
        onClose={() => setResendCampaignDialogOpen(false)}
        aria-labelledby="resend-campaign-dialog-title"
        aria-describedby="resend-campaign-dialog-description"
      >
        <DialogTitle id="resend-campaign-dialog-title">
          Resend Campaign
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="resend-campaign-dialog-description">
            Are you sure you want to resend this campaign to all recipients? This will send the notification again via all configured channels (in-app, push, email).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendCampaignDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleResendCampaignConfirm} color="primary" variant="contained" autoFocus>
            Resend
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for resending to specific recipient */}
      <Dialog
        open={resendRecipientDialogOpen}
        onClose={() => {
          setResendRecipientDialogOpen(false);
          setSelectedRecipient(null);
        }}
        aria-labelledby="resend-recipient-dialog-title"
        aria-describedby="resend-recipient-dialog-description"
      >
        <DialogTitle id="resend-recipient-dialog-title">
          Resend to Recipient
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="resend-recipient-dialog-description">
            Are you sure you want to resend this campaign to{" "}
            <strong>{selectedRecipient?.user_name || selectedRecipient?.user_email || "this recipient"}</strong>?
            This will send the notification again via all configured channels (in-app, push, email).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setResendRecipientDialogOpen(false);
              setSelectedRecipient(null);
            }}
            color="secondary"
          >
            Cancel
          </Button>
          <Button onClick={handleResendToRecipientConfirm} color="primary" variant="contained" autoFocus>
            Resend
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default NotificationCampaignDetail;

